<template>
  <div class="ca-bullet-container">
    <nav aria-label="Ações rápidas">
      <div
        v-if="cards.length"
        class="ca-quick-action-cards grid grid-cols-2"
        :style="{ '--ca-desktop-cols': desktopColumns }"
      >
        <CaQuickActionCard
          v-for="(card, index) in cards"
          :key="index"
          class="ca-quick-action-cards__card"
          :class="{ 'ca-quick-action-cards__card--full': isFullWidth(index) }"
          :title="card.title"
          :title-heading-type="card.titleHeadingType"
          :description="card.description"
          :description-heading-type="card.descriptionHeadingType"
          :backgroundColor="normalizeColor(card.color)"
          :image="card.iconUrl"
          :avatarBackgroundColorClass="card.iconBackgroundColor"
          :url="card.url"
          :target="card.target"
        />
      </div>
    </nav>
  </div>
</template>

<script>
const isDev = import.meta.env.DEV;
</script>

<script setup>
import { ref, computed, onMounted } from "vue";
import CaQuickActionCard from "../../base/cards/CaQuickActionCard.vue";

const props = defineProps({
  model: {
    type: Object,
    default: null,
  },
});

// Para import dinâmico em dev, evitando incluir o mock no bundle de produção
const modelRef = ref(props.model ?? null);
const model = computed(() => modelRef.value || {});

const cards = computed(() => model.value.cards ?? []);

const backgroundVariants = {
  laranja: "laranja",
  verde: "verde",
  branco: "branco",
};

/** Normalize a model color (e.g. "verde") to the base card variant. */
function normalizeColor(color) {
  return backgroundVariants[String(color || "").toLowerCase()] ?? "branco";
}

/**
 * When the number of cards is odd, the last card spans the full width
 * (both columns). Covers the 1-card and 3-card cases.
 */
function isFullWidth(index) {
  const total = cards.value.length;
  return total % 2 === 1 && index === total - 1;
}

/**
 * On desktop the grid has as many columns as there are cards (capped at 4),
 * so 1, 2 or 3 cards always fill the full width of the container.
 */
const desktopColumns = computed(() => Math.min(cards.value.length || 1, 4));

/** Container background variants (includes a neutral/grey option). */
const containerBackgroundVariants = {
  white: "white",
  solarized: "solarized",
};

onMounted(async () => {
  // Em desenvolvimento, se não receber um model via props, carrega o mock para facilitar testes
  if (isDev && !modelRef.value) {
    const { mockActionCards3 } =
      await import("./mocks/quickActionCardsMocks.js");
    modelRef.value = mockActionCards3;
  }
});
</script>

<style scoped lang="scss">
// ---------------------------------------------------------------------------
// Container (mobile first) — layout uses DS utility classes in the template
// (flex, flex-col, p-size-4, gap-size-3, rounded-size-4).
// ---------------------------------------------------------------------------
// Odd number of cards: the last one spans both columns (mobile 2-col grid).
.ca-quick-action-cards {
  // Espaçamento base (mobile e desktop): --size-5.
  gap: var(--size-5);
  padding: 24px 0 24px 0;
}

// Tablet (768px – 1024px): espaçamento reduzido para --size-4.
@media (min-width: 768px) and (max-width: 1024px) {
  .ca-quick-action-cards {
    gap: var(--size-4);
  }
}

.ca-quick-action-cards__card--full {
  grid-column: 1 / -1;
}

@media (min-width: 1024px) {
  // Desktop padding uses the DS spacing token (the lg:p-size-8 utility
  // isn't emitted by the DS stylesheet nor by the local Tailwind config).
  .ca-quick-action-cards {
    // As many columns as cards (capped at 4) so 1/2/3 cards fill the width.
    grid-template-columns: repeat(var(--ca-desktop-cols, 4), minmax(0, 1fr));
    // Padding vertical maior no desktop.
    padding: 48px 0 48px 0;
  }

  // On desktop, don't force the last card to full width.
  .ca-quick-action-cards__card--full {
    grid-column: auto;
  }
}

// ---------------------------------------------------------------------------
// Container background variants
// ---------------------------------------------------------------------------
.ca-quick-action-cards--white {
  background: var(--ca-color-neutrals-inverted);
}

.ca-quick-action-cards--solarized {
  background: var(--ca-color-backgrounds-container);
}

// ---------------------------------------------------------------------------
// Rounded corners variants
// ---------------------------------------------------------------------------
.ca-quick-action-cards--rounded-top {
  border-top-left-radius: var(--size-5);
  border-top-right-radius: var(--size-5);
}

.ca-quick-action-cards--rounded-bottom {
  border-bottom-left-radius: var(--size-5);
  border-bottom-right-radius: var(--size-5);
}

@media (min-width: 1024px) {
  .ca-quick-action-cards--rounded-top {
    border-top-left-radius: var(--size-8);
    border-top-right-radius: var(--size-8);
  }

  .ca-quick-action-cards--rounded-bottom {
    border-bottom-left-radius: var(--size-8);
    border-bottom-right-radius: var(--size-8);
  }
}
</style>
